import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, X, Paperclip } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge, ProgressBar } from "./CampaignStatus";

const FILTERS = [["", "Wszystkie"], ["sent", "Wysłane"], ["failed", "Błędy"], ["pending", "Oczekujące"]];
const D_CLS = { sent: "text-emerald-700", failed: "text-red-600", pending: "text-blue-600" };
const D_LABEL = { sent: "wysłano", failed: "błąd", pending: "oczekuje" };

export const CampaignDetailModal = ({ id, onClose, onChanged }) => {
    const [c, setC] = useState(null);
    const [filter, setFilter] = useState("");

    const load = useCallback(() => api.get(`/mailing/campaigns/${id}`, { params: { status: filter } }).then((r) => setC(r.data)), [id, filter]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (c?.status !== "sending") return undefined;
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, [c?.status, load]);

    const retry = async () => {
        try {
            const { data } = await api.post(`/mailing/campaigns/${id}/retry`);
            toast.success(`Ponawiam ${data.retrying} wysyłek.`);
            await load(); onChanged?.();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="mailing-campaign-modal">
                {!c ? <p className="p-8 text-gray-500">Ładowanie…</p> : (
                    <>
                        <div className="px-6 pt-6 pb-4 border-b space-y-3">
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                    <StatusBadge status={c.status} testid="mailing-campaign-modal-status" />
                                    <h3 className="font-serif text-2xl text-[#092E22] mt-2 break-words">{c.subject}</h3>
                                    <p className="text-xs text-gray-500 mt-1">{c.sent_at ? `Wysłano ${new Date(c.sent_at).toLocaleString("pl-PL")} przez ${c.sent_by}` : "Szkic"}</p>
                                </div>
                                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="mailing-campaign-modal-close"><X size={18} /></button>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center" data-testid="mailing-campaign-stats">
                                {[["Odbiorców", c.total, "text-[#092E22]"], ["Wysłano", c.sent, "text-emerald-700"], ["Błędy", c.failed, "text-red-600"]].map(([l, v, cls]) => (
                                    <div key={l} className="rounded-2xl bg-[#FAF8F3] py-3"><p className={`text-2xl font-semibold ${cls}`}>{v || 0}</p><p className="text-[10px] uppercase tracking-wider text-gray-500">{l}</p></div>
                                ))}
                            </div>
                            <ProgressBar sent={c.sent} failed={c.failed} total={c.total} />
                            {c.error && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{c.error}</p>}
                            {c.attachments?.length > 0 && (
                                <p className="text-xs text-gray-500 flex items-center gap-1 flex-wrap"><Paperclip size={12} /> {c.attachments.map((a) => a.filename).join(", ")}</p>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                                {FILTERS.map(([k, l]) => (
                                    <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`mailing-delivery-filter-${k || "all"}`}
                                        className={`px-3 py-1 rounded-full text-xs ${filter === k ? "bg-[#0D4F3C] text-white" : "bg-gray-100 text-gray-700"}`}>{l}</button>
                                ))}
                                {["sent", "partial", "failed"].includes(c.status) && c.failed > 0 && (
                                    <Button size="sm" onClick={retry} className="ml-auto rounded-full bg-[#D4A537] hover:bg-[#c4952b] text-[#092E22]" data-testid="mailing-retry-btn">
                                        <RotateCcw size={13} className="mr-1.5" /> Ponów nieudane ({c.failed})
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="overflow-y-auto px-6 py-3" data-testid="mailing-deliveries">
                            {c.deliveries.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">Brak pozycji.</p>}
                            {c.deliveries.map((d) => (
                                <div key={d.id} className="py-2 border-b border-gray-50 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4" data-testid={`mailing-delivery-${d.email}`}>
                                    <span className="font-mono text-xs text-[#092E22] sm:w-64 truncate">{d.email}</span>
                                    <span className={`text-xs font-semibold uppercase ${D_CLS[d.status]}`}>{D_LABEL[d.status]}</span>
                                    {d.error && <span className="text-xs text-red-500 break-all sm:flex-1">{d.error}</span>}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CampaignDetailModal;
